import React, { useRef, useEffect, useMemo } from "react";
import { useFormikContext } from "formik";
import { debounce } from "lodash";
import { FormikField } from "../Fields/Field";
import { makeMatchingSearchRequest } from "../../api";
import { handleResultKeyDown } from "./employerMatchingHelpers";
import SearchResults from "./SearchResults";

/**
 * @param {object} props
 * @param {string} props.spinnerColor
 * @param {string} props.path
 * @param {string} props.employerField
 * @param {Function} props.handleCancelButton
 * @param {boolean} props.isDtD
 * @param {boolean} props.searchEnabled
 * @param {boolean} props.searching
 * @param {object} props.selection
 * @param {Function} props.setSelection
 * @param {Function} props.setSearching
 * @param {Function} props.setSearchResults
 * @param {string} props.matchingEmployerLabel
 * @param {string} props.matchingEmployerHelpText
 * @param {Function} props.setSearchActive
 * @param {Array} props.searchResults
 * @param {Function} props.setMatchConfirmed
 * @param {Function} props.updateReduxValues
 * @param {boolean} props.focusInput
 * @param {Function} props.setFocusInput
 * @returns {React.JSX.Element}
 */
const EmployerLookup = ({
    spinnerColor,
    path,
    employerField,
    handleCancelButton,
    isDtD,
    searchEnabled,
    searching,
    selection,
    setSelection,
    setSearching,
    setSearchResults,
    matchingEmployerLabel,
    matchingEmployerHelpText,
    setSearchActive,
    searchResults,
    setMatchConfirmed,
    updateReduxValues,
    focusInput,
    setFocusInput,
}) => {
    const { handleChange, values, setValues } = useFormikContext();

    useEffect(() => {
        let isComponentMounted = true;

        if (isComponentMounted) {
            if (selection.id && selection.id !== "no match") {
                const formikUpdateValues = {
                    ...values,
                    Employer: selection.company_name,
                    Matching_Company_Name: selection.company_name,
                    Matching_Company_ID: selection.id,
                    Matching_Parent_Raw_ID: selection.parent_raw_id,
                    Matching_Contact_Address_Line_1:
                        selection.contact_address_line1,
                    Matching_Contact_Address_Line_2:
                        selection.contact_address_line2,
                    Matching_Contact_City: selection.contact_city,
                    Matching_Contact_State: selection.contact_state,
                    Matching_Contact_Zip: selection.contact_zip,
                    Matching_Contact_Phone: selection.contact_phone,
                    Matching_Contact_Email: selection.contact_email,
                    Matching_Donor_Found: selection.donorExists,
                    Matching_Donor_Verified: selection.donorVerified,
                };
                setSearchActive(false);
                setMatchConfirmed(true);
                setSearchResults({});
                setValues(formikUpdateValues);
                updateReduxValues(formikUpdateValues);
            }
        }
        // Cleanup function that is run when the component dismounts thus
        // preventing a memory leak by ensuring that the component state will
        // not update if the component has been dismounted by the time the
        // async request completes
        return () => {
            isComponentMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection, setSelection]);

    // focuses the search field in the event that the user has cleared a previous entry
    useEffect(() => {
        if (focusInput) {
            document.getElementById("Employer")?.focus();
        }
        return () => {
            setFocusInput(false);
        };
    }, [focusInput, setFocusInput]);

    const handleResultClick = (companySelected) => {
        setSelection(companySelected);
    };

    const handleFocus = () => {
        setSearchActive(true);
    };

    const handleBlur = () => {
        setSearchActive(false);
    };

    const handleSearch = () => {
        const postData = {
            formKey: path,
            formType: "1",
            prefix: employerField || "",
            csrfToken: document.getElementById("csrfToken")?.value,
        };
        setSearchResults([]);
        if (searchEnabled && !searching) {
            setSearching(true);

            makeMatchingSearchRequest(postData).then((response) => {
                setSearching(false);

                if (!response || !response?.data?.success) {
                    setSearchResults([{ id: "no match" }]);
                }

                if (response?.data?.result) {
                    setSearchResults(response.data.result);
                }
            });
        }
    };

    // stores mutable reference to handleSearch
    const ref = useRef(handleSearch);

    // upon change of the employer make sure that our reference is using the most
    // up to date version of handleSearch
    useEffect(() => {
        ref.current = handleSearch;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employerField]);

    // creates debounce callback only once on mount
    const debouncedCallback = useMemo(() => {
        const currentRef = () => {
            ref.current?.();
        };
        return debounce(currentRef, 500);
    }, []);

    // handle key down event using debounced callback
    // ensures the function is only called once and does
    // not fire on every key press after the delay defined
    // on the debounce.
    const handleKeyDown = ({ key }) => {
        if (
            key === "Tab" ||
            key === "Shift" ||
            key === "ArrowDown" ||
            key === "ArrowUp" ||
            key === "Enter" ||
            key === "ArrowLeft" ||
            key === "ArrowRight"
        ) {
            return;
        }
        debouncedCallback();
    };

    const displayResults =
        (searching && searchResults.length === 0) ||
        (!searching && searchResults.length > 0);

    return (
        <>
            <FormikField
                type="text"
                name="Employer"
                onChange={handleChange}
                handleFocus={handleFocus}
                handleBlur={handleBlur}
                placeholder="Employer"
                id="Employer"
                field="EMPLOYER"
                descText="Optional"
                helpText={matchingEmployerHelpText}
                label={matchingEmployerLabel}
                onKeyDown={handleKeyDown}
                role="combobox"
                ariaExpanded={displayResults ? "true" : "false"}
            />
            <SearchResults
                handleResultClick={handleResultClick}
                searching={searching}
                searchResults={searchResults}
                spinnerColor={spinnerColor}
                isDtD={isDtD}
                handleCancelButton={handleCancelButton}
                handleResultKeyDown={handleResultKeyDown}
            />
        </>
    );
};

export default EmployerLookup;
